// Pure helpers for JOIN edit modules (add/remove/modify JOIN). Lives in
// edit/edits/ so multiple JOIN-action modules can share them.
//
// `formatJoinTargetText(candidate, engine)` produces the SQL text that
// `add-join` injects as `targetText` — `schema.table alias`, each part in its
// catalog spelling, delimited when it must be (`"order items" oi`,
// `dbo.[Order Details] od`). `formatJoinOnText(candidate, …)` builds the ON
// predicate from the FK edge's column pairs, qualifying the left side the way
// the query spells its table (its alias, else its delimited name token), and
// for a loose relationship with source / target filters takes the resolver's
// pre-built predicate verbatim (the new relation already under its alias).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN +
// edit ON predicate.

import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import { foldFor } from '../../../../sql-resolver/service/schemaProjectionHelpers';
import { sameRelation, type RelationIdentity } from '../../../../sql-resolver/service/snapshotRelations';
import { catalogIdentSql, catalogRelationSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import { deriveShortAlias, disambiguateAlias } from '../../../../sql-resolver/suggest/suggestJoinCandidates';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { JoinCandidate, SchemaSnapshot } from '../../../../sql-resolver/types/schema';
import type { AliasRelation, QualifyColumn } from '../../../../sql-resolver/types/structuralEdit';
import { tableSuggestionKey, tableSuggestionLabel } from './fromItemHelpers';
import { findRelationSource, sourceColumns, sourceRelation, type QuerySource } from '../../../../sql-resolver/scope/querySources';

export function formatJoinTargetText(candidate: JoinCandidate, engine: SqlEngine): string {
  return `${catalogRelationSql(candidate.rightTable.qualified, engine)} ${candidate.suggestedAlias}`;
}

// The FK edge's left table as the query qualifies it (its source's qualifier);
// the passed anchor alias when the table isn't among the sources. Matched by
// relation identity (schema included, the name under the snapshot's fold), so
// `mia_s1.orders a` joined to `mia_s2.orders` resolves to `a`, and the two
// are not "the same relation".
function resolveLeftQualifier(
  candidate: JoinCandidate,
  fallbackAlias: string,
  sources: readonly QuerySource[],
  engine: SqlEngine,
): { sql: string; relation: RelationIdentity } {
  const fold = foldFor(engine);
  const leftTable = candidate.leftTable;
  const ref: RelationIdentity = {
    foldedName: fold(leftTable.name),
    ...(leftTable.schema ? { foldedSchema: fold(leftTable.schema) } : {}),
  };
  const source = findRelationSource(sources.filter((s) => !s.outer), ref);
  return { sql: source?.qualifierSql ?? fallbackAlias, relation: (source && sourceRelation(source)) ?? ref };
}

export function formatJoinOnText(
  candidate: JoinCandidate,
  leftAlias: string,
  engine: SqlEngine,
  sources: readonly QuerySource[] = [],
): string {
  const { leftColumns, rightColumns, onPredicate } = candidate.fkEdge;
  // JSON-path loose rels and filtered loose rels carry a pre-formatted, fully
  // alias-qualified ON predicate (the alias is *inside* the engine
  // JSON-extract; the source / target filters are qualified per side, the new
  // relation already by `suggestedAlias`). It is emitted verbatim — never
  // re-prefix an alias onto the bare column, and never substitute into it: a
  // filter literal may read like a snippet placeholder. Real FKs and plain
  // loose rels never set `onPredicate`.
  if (onPredicate !== undefined) return onPredicate;
  const left = resolveLeftQualifier(candidate, leftAlias, sources, engine);
  // Same-relation guard: never emit `x.a = x.b` — the join target appears on
  // both sides. Emit empty ON so the manual column picker / inline editor opens.
  if (sameRelation(left.relation, candidate.rightTable.qualified) || left.sql === candidate.suggestedAlias) {
    return '';
  }
  // Always written `<right>.<col> = <left>.<col>` — the right alias is the
  // newly-suggested alias, the left qualifier the FK edge's real left table.
  const conds: string[] = [];
  for (let i = 0; i < leftColumns.length; i++) {
    const r = rightColumns[i];
    const l = leftColumns[i];
    if (r === undefined || l === undefined) continue;
    conds.push(`${candidate.suggestedAlias}.${catalogIdentSql(r, engine)} = ${left.sql}.${catalogIdentSql(l, engine)}`);
  }
  return conds.join(' AND ');
}

/** A relation already in the statement: its name and, when it has one, its alias. */
export type InQueryRelation = { readonly qualified: QualifiedName; readonly alias?: string };

/**
 * The statement's FROM relations and joined relations (derived tables / CTE
 * refs carry no schema columns). With `engine`, each name's `foldedName` /
 * `foldedSchema` is the schema snapshot's key for it — the snapshot folds the
 * catalog name whatever the query's quoting, so `[Order Details]` must match
 * `order details`.
 */
export function inQueryRelations(
  structure: QueryStructure | null | undefined,
  engine?: SqlEngine,
): InQueryRelation[] {
  if (!structure) return [];
  const fold = engine ? foldFor(engine) : undefined;
  const items = [...(structure.fromItems ?? []), ...(structure.joins ?? []).map((j) => j.right)];
  return items.flatMap((item) => {
    if (item.target.kind !== 'relation') return [];
    const q = item.target.qualified;
    const qualified = fold
      ? { ...q, foldedName: fold(q.name), ...(q.schema ? { foldedSchema: fold(q.schema) } : {}) }
      : q;
    return [{ qualified, alias: item.alias?.alias }];
  });
}

/**
 * Every column a bare reference in the statement can mean: each name that
 * exists in exactly one base table already in the statement, paired with that
 * table's qualifier as the query spells it (its alias, else its delimited name
 * token). The `add-join` edit qualifies all their bare references, so after a
 * join the SQL and the chips read one way (`c.id, c.name`) and
 * `SELECT id, name FROM customers` + JOIN orders still runs (round 1: `id`
 * would otherwise be ambiguous). A name two tables share is left alone — a
 * bare use of it was already ambiguous.
 */
export function joinColumnQualifiers(
  sources: readonly QuerySource[],
  snapshot: SchemaSnapshot | undefined,
): QualifyColumn[] {
  const relations = sources.filter((s) => s.kind === 'relation' && !s.outer);
  if (!snapshot || relations.length === 0) return [];
  const inQuery = relations.map((relation) => ({
    exposedName: relation.qualifierSql,
    columns: sourceColumns(relation, snapshot),
  }));
  const owners = new Map<string, number>();
  for (const relation of inQuery) {
    for (const column of new Set(relation.columns.map((c) => c.qualified.name.toLowerCase()))) {
      owners.set(column, (owners.get(column) ?? 0) + 1);
    }
  }
  const out: QualifyColumn[] = [];
  for (const relation of inQuery) {
    for (const column of relation.columns) {
      if (owners.get(column.qualified.name.toLowerCase()) === 1) {
        out.push({ name: column.qualified.name, qualifier: relation.exposedName });
      }
    }
  }
  return out;
}

/** Short words a derived alias must never be — `on`, `as`… would break the SQL. */
const RESERVED_SHORT_ALIASES: ReadonlySet<string> = new Set([
  'as', 'at', 'by', 'do', 'go', 'if', 'in', 'is', 'no', 'of', 'on', 'or', 'to',
]);

/**
 * Aliases for the statement's unaliased relations when a join introduces one
 * (`FROM customers` + `JOIN orders o` → `customers c`), so the SQL reads one
 * way. Derived like join aliases (initials) and never equal to a name already
 * exposed, to the join's own alias, or to a short keyword. SELECT only — the
 * planner declines anything it cannot rewrite safely.
 */
export function baseRelationAliases(
  structure: QueryStructure | null | undefined,
  joinAlias: string,
): AliasRelation[] {
  if (!structure || structure.kind !== 'select') return [];
  const fromItems = structure.fromItems ?? [];
  const joins = structure.joins ?? [];
  const exposed = [...fromItems, ...joins.map((j) => j.right)].flatMap((item) => {
    if (item.alias) return [item.alias.alias.toLowerCase()];
    return item.target.kind === 'relation' ? [item.target.qualified.name.toLowerCase()] : [];
  });
  const used = new Set([...exposed, joinAlias.toLowerCase(), ...RESERVED_SHORT_ALIASES]);
  const out: AliasRelation[] = [];
  const add = (source: AliasRelation['source'], index: number, name: string): void => {
    const alias = disambiguateAlias(deriveShortAlias(name), used);
    used.add(alias);
    out.push({ source, index, alias });
  };
  fromItems.forEach((item, index) => {
    if (!item.alias && item.target.kind === 'relation') add('from', index, item.target.qualified.name);
  });
  joins.forEach((join, index) => {
    if (!join.right.alias && join.right.target.kind === 'relation') add('join', index, join.right.target.qualified.name);
  });
  return out;
}

export function joinSuggestionKey(candidate: JoinCandidate): string {
  return `${tableSuggestionKey(candidate.rightTable)}::${candidate.suggestedAlias}`;
}

export function joinSuggestionLabel(candidate: JoinCandidate): string {
  return `${tableSuggestionLabel(candidate.rightTable)} ${candidate.suggestedAlias}`;
}
